import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  X, 
  Building2, 
  Ticket, 
  Users, 
  Star,
  CheckCircle2,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { apiClient } from '../lib/apiClient';
import { Button } from './ui/button';

interface ZendeskOrganization {
  id: string;
  name: string;
  details?: string;
  domain_names?: string[];
  ticket_count?: number;
}

interface ZendeskImportModalProps {
  onClose: () => void;
}

const ZendeskImportModal: React.FC<ZendeskImportModalProps> = ({ onClose }) => {
  const [selectedOrgs, setSelectedOrgs] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<'organizations' | 'all'>('organizations');
  const queryClient = useQueryClient();

  // Fetch Zendesk organizations
  const { data: organizationsData, isLoading: orgsLoading, error: orgsError } = useQuery({
    queryKey: ['zendesk-organizations'],
    queryFn: () => apiClient.customRequest('/api/integrations/zendesk/organizations'),
    retry: 1
  });

  // Import mutation
  const importMutation = useMutation({
    mutationFn: async (data: { organization_ids?: string[]; import_all?: boolean }) => {
      return apiClient.customRequest('/api/integrations/zendesk/import', {
        method: 'POST',
        body: data
      });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      const message = data.data 
        ? `Successfully imported ${data.data.tickets || 0} tickets, ${data.data.users || 0} users, ${data.data.organizations || 0} organizations!`
        : 'Zendesk data imported successfully!';
      alert(message);
      onClose();
    },
    onError: (error: any) => {
      alert(`Failed to import Zendesk data: ${error.message}`);
    }
  });

  const organizations = organizationsData?.organizations || [];

  const handleOrgToggle = (orgId: string) => {
    const newSelected = new Set(selectedOrgs);
    if (newSelected.has(orgId)) {
      newSelected.delete(orgId);
    } else {
      newSelected.add(orgId);
    }
    setSelectedOrgs(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedOrgs.size === organizations.length) {
      setSelectedOrgs(new Set());
    } else {
      setSelectedOrgs(new Set(organizations.map((org: ZendeskOrganization) => org.id)));
    }
  };

  const handleImport = () => {
    if (activeTab === 'all') {
      importMutation.mutate({ import_all: true });
    } else if (selectedOrgs.size > 0) {
      importMutation.mutate({ organization_ids: Array.from(selectedOrgs) });
    } else {
      alert('Please select at least one organization to import');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-teal-600 rounded-lg flex items-center justify-center">
              <Building2 className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Import Zendesk Data</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Select organizations or import all support data
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700 px-6">
          <button
            onClick={() => setActiveTab('organizations')}
            className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'organizations'
                ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              <span>By Organization ({organizations.length})</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('all')}
            className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'all'
                ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />
              <span>Import All Data</span>
            </div>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'organizations' ? (
            <div>
              {/* Select All */}
              <div className="mb-4 flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedOrgs.size === organizations.length && organizations.length > 0}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-blue-600 rounded border-gray-300 dark:border-gray-600"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Select All Organizations
                  </span>
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {selectedOrgs.size} of {organizations.length} selected
                </span>
              </div>

              {/* Organizations List */}
              {orgsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">Loading organizations...</p>
                  </div>
                </div>
              ) : orgsError ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <AlertCircle className="h-12 w-12 text-red-500 mb-3" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                    Zendesk authorization required
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md">
                    We couldn't access your Zendesk organizations. Your Zendesk token may be expired or revoked.
                    Please reconnect Zendesk from the Integrations page and try again.
                  </p>
                </div>
              ) : organizations.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <AlertCircle className="h-12 w-12 text-gray-400 dark:text-gray-500 mb-3" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                    No Organizations Found
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No organizations available in your Zendesk account
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {organizations.map((org: ZendeskOrganization) => (
                    <label
                      key={org.id}
                      className="flex items-start gap-3 p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={selectedOrgs.has(org.id)}
                        onChange={() => handleOrgToggle(org.id)}
                        className="w-4 h-4 text-blue-600 rounded border-gray-300 dark:border-gray-600 mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium text-gray-900 dark:text-white">{org.name}</h4>
                          {org.ticket_count !== undefined && (
                            <span className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full">
                              {org.ticket_count} tickets
                            </span>
                          )}
                        </div>
                        {org.domain_names && org.domain_names.length > 0 && (
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {org.domain_names.join(', ')}
                          </p>
                        )}
                        {org.details && (
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 line-clamp-2">
                            {org.details}
                          </p>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="py-8">
              <div className="max-w-2xl mx-auto text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  Import All Zendesk Data
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  This will import all tickets, users, organizations, and satisfaction ratings from the last 90 days.
                </p>
                
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <Ticket className="h-6 w-6 text-blue-600 dark:text-blue-400 mx-auto mb-2" />
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Tickets</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Last 90 days</p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <Users className="h-6 w-6 text-green-600 dark:text-green-400 mx-auto mb-2" />
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Users</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">All users</p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <Building2 className="h-6 w-6 text-purple-600 dark:text-purple-400 mx-auto mb-2" />
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Organizations</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">All organizations</p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <Star className="h-6 w-6 text-yellow-600 dark:text-yellow-400 mx-auto mb-2" />
                    <p className="text-sm font-medium text-gray-900 dark:text-white">CSAT Ratings</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Last 90 days</p>
                  </div>
                </div>

                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    💡 <strong>Tip:</strong> After the initial import, new data will be automatically synced via webhooks in real-time.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {activeTab === 'organizations' ? (
              <span>
                {selectedOrgs.size > 0 
                  ? `${selectedOrgs.size} organization${selectedOrgs.size > 1 ? 's' : ''} selected`
                  : 'Select organizations to import their tickets and data'}
              </span>
            ) : (
              <span>Complete historical sync (last 90 days)</span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={importMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleImport}
              disabled={importMutation.isPending || (activeTab === 'organizations' && selectedOrgs.size === 0)}
              className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white"
            >
              {importMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  {activeTab === 'all' ? 'Import All Data' : `Import ${selectedOrgs.size} Organization${selectedOrgs.size !== 1 ? 's' : ''}`}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ZendeskImportModal;

